import { FAIcon } from '@/components'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { cn } from '@/lib/utils'
import { useTranslate } from '@ur-solutions/react-hooks'
import React from 'react'

export type DetailPillTone = 'emerald' | 'amber' | 'rose' | 'stone'

const PILL_TONES: Record<DetailPillTone, string> = {
  emerald: 'border-emerald-300 bg-emerald-100 text-emerald-900',
  amber: 'border-amber-300 bg-amber-100 text-amber-900',
  rose: 'border-rose-300 bg-rose-100 text-rose-900',
  stone: 'border-stone-300 bg-stone-100 text-stone-800',
}

export const DetailPill: React.FC<{
  tone: DetailPillTone
  dotColor?: string
  children: React.ReactNode
}> = ({ tone, dotColor, children }) => (
  <span
    className={cn(
      PILL_TONES[tone],
      'inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-semibold'
    )}
  >
    <span
      className="h-1.5 w-1.5 rounded-full bg-current"
      style={dotColor ? { background: dotColor } : undefined}
    />
    {children}
  </span>
)

export interface DetailField {
  label: string
  value: React.ReactNode
  hint?: React.ReactNode
  wide?: boolean
}

interface HeaderProps {
  eyebrow: string
  title: string
  /** The broken rule, on its own row under the title. */
  rule: string
  pills: React.ReactNode
  /** What the day-to-day follow-up needs, shown in the card. */
  fields: DetailField[]
  /** Everything else, behind the info icon in the card's corner. */
  info?: DetailField[]
  children?: React.ReactNode
}

/** The main card of an infringement's detail page, in the Absence page's
 * layout: eyebrow, title and status, the broken rule on its own row, a grid of
 * fields, then text blocks. The page's actions live in its header, not in the
 * card. */
export const InfringementDetailHeader: React.FC<HeaderProps> = ({
  eyebrow,
  title,
  rule,
  pills,
  fields,
  info = [],
  children,
}) => {
  const translations = useTranslate({ rule: 'infringements.rule' })

  return (
    <section className="overflow-hidden rounded-2xl bg-white shadow-card">
      <div className="flex flex-col gap-4 px-5 py-5 sm:px-6 sm:py-6 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0 space-y-1">
          <p className="text-xs font-semibold tracking-[0.22em] text-stone-500 uppercase">
            {eyebrow}
          </p>
          <h2 className="text-2xl font-semibold text-stone-900">{title}</h2>
        </div>

        <div className="flex flex-wrap items-center gap-2 self-start lg:justify-end">
          {pills}
          {info.length > 0 && <DetailInfo fields={info} />}
        </div>
      </div>

      <div className="border-t border-stone-200 px-5 py-4 sm:px-6">
        <p className="text-xs font-semibold text-stone-500 uppercase">
          {translations.rule}
        </p>
        <p className="mt-1 text-base font-medium text-stone-900">{rule}</p>
      </div>

      <div className="overflow-hidden border-t border-stone-200">
        <dl className="-mr-px -mb-px grid sm:grid-cols-[repeat(auto-fit,minmax(14rem,1fr))]">
          {fields.map(field => (
            <div
              key={field.label}
              className={cn(
                'space-y-1 border-r border-b border-stone-200 px-5 py-4 sm:px-6',
                field.wide && 'sm:col-span-2'
              )}
            >
              <dt className="text-xs font-semibold text-stone-500 uppercase">
                {field.label}
              </dt>
              <dd className="text-sm font-medium text-stone-900">
                {field.value ?? '-'}
              </dd>
              {field.hint && (
                <dd className="text-xs text-stone-500">{field.hint}</dd>
              )}
            </div>
          ))}
        </dl>
      </div>

      {children}
    </section>
  )
}

export const DetailTextBlock: React.FC<{
  label: string
  action?: React.ReactNode
  children: React.ReactNode
}> = ({ label, action, children }) => (
  <div className="border-t border-stone-200 px-5 py-4 sm:px-6">
    <div className="flex items-center justify-between gap-3">
      <p className="text-xs font-semibold text-stone-500 uppercase">{label}</p>
      {action}
    </div>
    <div className="mt-1.5 text-sm whitespace-pre-wrap text-stone-800">
      {children}
    </div>
  </div>
)

export const WronglyGeneratedNotice: React.FC<{
  title: string
  explanationLabel: string
  explanation: string | null | undefined
}> = ({ title, explanationLabel, explanation }) => (
  <div className="rounded-2xl border border-amber-200 bg-amber-50 px-5 py-4 text-amber-900 sm:px-6">
    <p className="text-sm font-semibold">{title}</p>
    {explanation && (
      <p className="mt-1 text-sm">
        <span className="font-semibold">{explanationLabel}:</span> {explanation}
      </p>
    )}
  </div>
)

const DetailInfo: React.FC<{ fields: DetailField[] }> = ({ fields }) => {
  const translations = useTranslate({ details: 'common.details' })

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={translations.details}
          className="flex size-8 items-center justify-center rounded-full text-stone-500 transition hover:bg-stone-100 hover:text-stone-800 focus-visible:ring-2 focus-visible:ring-stone-300 focus-visible:outline-none"
        >
          <FAIcon icon="circle-info" type="regular" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 max-w-[90vw] p-0">
        <dl className="divide-y divide-stone-200">
          {fields.map(field => (
            <div key={field.label} className="space-y-1 px-4 py-3">
              <dt className="text-xs font-semibold text-stone-500 uppercase">
                {field.label}
              </dt>
              <dd className="text-sm whitespace-pre-wrap text-stone-900">
                {field.value ?? '-'}
              </dd>
              {field.hint && (
                <dd className="text-xs text-stone-500">{field.hint}</dd>
              )}
            </div>
          ))}
        </dl>
      </PopoverContent>
    </Popover>
  )
}
